import { Badge } from 'core/components/ui/Badge'
import { Modal } from 'core/components/ui/Modal'
import { userRepository } from 'features/user-management/data/repositories/UserRepositoryImpl'
import React, { useEffect, useState } from 'react'
import { GrievanceRecord } from '../../domain/entities/Grievance'

interface AssignAmModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (id: string, amId: string, amName: string) => Promise<void>
  record: GrievanceRecord | null
}

export function AssignAmModal({ isOpen, onClose, onSubmit, record }: AssignAmModalProps) {
  const [amId, setAmId] = useState('')
  const [ams, setAms] = useState<{ id: string; name: string }[]>([])
  const [loadingAms, setLoadingAms] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (isOpen) {
      setAmId('')
      setErrorMsg('')

      const fetchAMs = async () => {
        setLoadingAms(true)
        try {
          const res = await userRepository.getUsers({ role: 'account_manager', page: 1, limit: 100 })
          setAms(res.users.map((u) => ({ id: u.id, name: u.name })))
        } catch (e) {
          // NOSONAR: Logging AM fetch failure for client diagnostics
          // eslint-disable-next-line no-console
          console.error('Failed to load AMs', e) // NOSONAR
        } finally {
          setLoadingAms(false)
        }
      }

      fetchAMs()
    }
  }, [isOpen])

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!record || !amId) return

    const matched = ams.find((am) => am.id === amId)
    if (!matched) return

    setSubmitting(true)
    setErrorMsg('')
    try {
      await onSubmit(record.id, matched.id, matched.name)
      onClose()
    } catch (err: unknown) {
      const errorVal = err as Error
      setErrorMsg(errorVal.message || 'Failed to assign account manager.')
    } finally {
      setSubmitting(false)
    }
  }

  const footerActions = (
    <div className="flex items-center justify-end gap-3 w-full">
      <button
        type="button"
        onClick={onClose}
        disabled={submitting}
        className="px-4 py-2 text-xs font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-all disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="assign-am-form"
        disabled={submitting || !amId || loadingAms}
        className="px-5 py-2 text-xs font-semibold bg-primary hover:bg-primary-hover text-primary-fg rounded-btn transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-1.5 active:scale-95"
      >
        {submitting ? (
          <>
            <svg className="animate-spin h-3.5 w-3.5 text-primary-fg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Assigning...</span>
          </>
        ) : (
          <>
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            <span>Assign Manager</span>
          </>
        )}
      </button>
    </div>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Account Manager" footer={footerActions} size="lg">
      <form id="assign-am-form" onSubmit={handleSubmit} className="space-y-5">
        {errorMsg && (
          <div className="p-3 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium flex items-center gap-2">
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Modern Card Container for Grievance & Order Details */}
        {record && (
          <div className="bg-card border border-border rounded-card p-4 space-y-4 shadow-sm">
            {/* Top Bar: IDs & Buyer Badge */}
            <div className="space-y-2 border-b border-border/60 pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse shrink-0" />
                  <span className="text-xs font-bold text-text-primary uppercase tracking-wider">
                    Grievance Details
                  </span>
                </div>
                {record.buyerType && (
                  <Badge tone="secondary" variant="outline" className="capitalize text-[10px] px-2 py-0.5 font-bold">
                    {record.buyerType}
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-[11px] text-text-secondary block font-medium">Grievance ID</span>
                  <span className="font-mono text-text-primary font-semibold break-all">{record.id}</span>
                </div>
                <div>
                  <span className="text-[11px] text-text-secondary block font-medium">Linked Order ID</span>
                  <span className="font-mono text-text-primary font-semibold break-all">{record.orderId}</span>
                </div>
              </div>
            </div>

            {/* Middle Section: Buyer Name & Deficit Issue */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="space-y-0.5">
                <span className="text-[11px] text-text-secondary block font-medium uppercase tracking-wider">
                  Buyer / Entity Name
                </span>
                <span className="font-bold text-text-primary text-sm block">{record.buyerName || record.raisedBy}</span>
              </div>

              <div className="space-y-1 sm:text-right">
                <span className="text-[11px] text-text-secondary block font-medium uppercase tracking-wider">
                  Deficit Issue
                </span>
                <div className="inline-flex flex-col sm:items-end gap-0.5">
                  <Badge tone="danger" variant="subtle" className="font-bold text-xs px-2.5 py-0.5">
                    {record.affectedUnits} Units Deficit
                  </Badge>
                  {record.orderedUnits > 0 && (
                    <span className="text-[10px] text-text-secondary font-mono">
                      Ordered: {record.orderedUnits} | Received: {record.receivedUnits}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Reason / Discrepancy Note */}
            {record.issue && (
              <div className="bg-bg/70 border border-border/60 rounded-btn p-3 space-y-1">
                <span className="text-[10px] font-bold text-text-secondary uppercase tracking-wider block">
                  Discrepancy Reason
                </span>
                <p className="text-xs text-text-primary leading-relaxed font-medium italic break-words">
                  {record.issue}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Choose Account Manager Section at the Bottom */}
        <div className="space-y-2 pt-1">
          <label className="text-xs font-bold text-text-primary uppercase tracking-wider flex items-center justify-between">
            <span>
              Choose Account Manager <span className="text-danger">*</span>
            </span>
            {ams.length > 0 && (
              <span className="text-[10px] text-text-secondary font-normal lowercase">
                ({ams.length} staff available)
              </span>
            )}
          </label>

          <div className="relative">
            <select
              value={amId}
              onChange={(e) => setAmId(e.target.value)}
              disabled={loadingAms || submitting}
              className="w-full px-3.5 py-2.5 border border-border rounded-btn bg-surface text-text-primary text-xs font-medium focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all disabled:opacity-50 appearance-none cursor-pointer pr-10"
            >
              <option value="">{loadingAms ? 'Loading account managers...' : '-- Select Account Manager --'}</option>
              {ams.map((am) => (
                <option key={am.id} value={am.id}>
                  {am.name} ({am.id})
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-text-secondary">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <p className="text-[11px] text-text-secondary">
            Selected staff will be assigned as the primary Account Manager to resolve this grievance.
          </p>
        </div>
      </form>
    </Modal>
  )
}
